/**
 * Marquee block
 * Duplicates children into a looping track and scrolls with CSS.
 */

const initializedMarquees = new WeakSet();
const marqueeInstances = new Map();
const MOBILE_BREAKPOINT = 768;

class MarqueeBlock {
  constructor(element) {
    this.element = element;
    this.contentEl = null;
    this.trackEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    if (initializedMarquees.has(element)) return;
    initializedMarquees.add(element);

    this.visibility = this.element.dataset.sliderVisibility || 'both';
    marqueeInstances.set(element, this);

    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
    this.checkVisibilityAndInit();
  }

  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
      if (this.isInitialized) this.updateShift();
    }, 150);
  }

  init() {
    this.contentEl = this.element.querySelector('.marquee-content');
    if (!this.contentEl) return;

    let slides;

    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter((el) => !excludedTags.includes(el.tagName));

      if (slides.length < 1) return;

      this.originalSlides = slides;
    }

    const track = document.createElement('div');
    track.className = 'marquee-track';

    const appendSet = (asClone) => {
      slides.forEach((slide) => {
        const node = asClone ? slide.cloneNode(true) : slide;
        node.classList.add('marquee-item');
        if (asClone) {
          node.removeAttribute('id');
          node.setAttribute('aria-hidden', 'true');
        }
        track.appendChild(node);
      });
    };

    appendSet(false);
    appendSet(true);

    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(track);
    this.trackEl = track;

    this.hydrateLazyImages(track);

    const speed = parseInt(this.element.dataset.speed, 10) || 3000;
    track.style.animationDuration = `${speed * Math.max(slides.length, 4)}ms`;
    this.updateShift();
    requestAnimationFrame(() => this.updateShift());

    this.element.setAttribute('data-marquee-initialized', 'true');
    this.isInitialized = true;
  }

  updateShift() {
    if (!this.trackEl) return;
    const shift = Math.round(this.trackEl.scrollWidth / 2);
    if (shift > 0) {
      this.trackEl.style.setProperty('--marquee-shift', `-${shift}px`);
    }
  }

  hydrateLazyImages(root) {
    root.querySelectorAll('img[data-src], img[data-lazy-image]').forEach((img) => {
      const src = img.getAttribute('data-src') || img.currentSrc || img.getAttribute('src');
      const srcset = img.getAttribute('data-srcset') || img.getAttribute('srcset');

      if (src) {
        img.src = src;
        img.removeAttribute('data-src');
      }
      if (srcset) {
        img.srcset = srcset;
        img.removeAttribute('data-srcset');
      }

      img.loading = 'eager';
      img.classList.add('loaded');
      img.classList.remove('opacity-0');
      img.style.opacity = '1';

      const placeholder = img.closest('.content-image')?.querySelector('[data-blur-placeholder]');
      if (placeholder) placeholder.remove();
    });
  }

  destroy() {
    if (this.trackEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach((slide) => {
        slide.classList.remove('marquee-item');
        this.contentEl.appendChild(slide);
      });
    }

    this.trackEl = null;
    this.element.removeAttribute('data-marquee-initialized');
    this.isInitialized = false;
  }

  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

function initMarqueeSliderBlocks() {
  document.querySelectorAll('[data-slider-block]').forEach((slider) => {
    if (!slider.querySelector('.marquee-content')) return;
    new MarqueeBlock(slider);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMarqueeSliderBlocks);
} else {
  initMarqueeSliderBlocks();
}

window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initMarqueeSliderBlocks();
  }
});

const initProductSlidersFromSliderModule = window.initSliderBlocks;
function initAllSliderBlocks() {
  if (typeof initProductSlidersFromSliderModule === 'function') {
    initProductSlidersFromSliderModule();
  }
  initMarqueeSliderBlocks();
}

window.MarqueeSliderBlock = MarqueeBlock;
window.initSliderBlocks = initAllSliderBlocks;
